import { environment } from "$lib/env";
import type { IBalance } from "$lib/interfaces/IBalance";
import { getCookie } from "$lib/utils/cookies";
import type { IUserMe } from "$lib/interfaces/IUserMe";

interface ApiResponse<T> {
  data: T;
  error?: string;
}

class NotificationService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = environment.apiUrl;
  }

  private async fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = new Headers(options.headers);

    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const token = getCookie("forza_token");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetch(url, { ...options, headers });
    const data = await response.json();

    if (!response.ok) {
      return { data: null as T, error: data.message || "An error occurred" };
    }

    return { data };
  }

  async getNotifications(): Promise<ApiResponse<IUserMe[]>> {
    return this.fetchWithAuth<IUserMe[]>("/user/notifications?page=0&size=20");
  }

  async setActiveCurrency(currency: string): Promise<ApiResponse<IBalance[]>> {
    return this.fetchWithAuth<IBalance[]>(
      `/user/crypto-currencies/set-as-active`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ cryptoCurrencyName: currency }),
      },
    );
  }

  async markNotificationAsRead(notificationId: number) {
    return this.fetchWithAuth(`/user/notifications/${notificationId}/viewed`, {
      method: "POST",
    });
  }
}

export const notificationService = new NotificationService();
